import React, { useState } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';

import { accountsApi } from '../../api';
import { keysToCamel } from '../../utils';
import type { AuthenticatedUser } from '../../types';
import SpinLoader from '../SpinLoader';

import './index.scss';
import messages from './messages';

interface UserEditPopUpProps {
  userMetadata: AuthenticatedUser | null;
  setIsProfileModalOpen: (isOpen: boolean) => void;
  setUserMetadata: (user: AuthenticatedUser) => void;
}

const FIELD_MAX_LENGTH = {
  fullName: 255,
  title: 255,
  bio: 300,
  socialLink: 100,
};

const SOCIAL_PLATFORM_URLS: Record<string, string[]> = {
  linkedin: ['linkedin.com/in/'],
  x: ['x.com/', 'twitter.com/'],
};

const SOCIAL_PLATFORM_LABELS: Record<string, string> = {
  linkedin: '"linkedin.com"',
  x: '"x.com"',
};

const UserEditPopUp: React.FC<UserEditPopUpProps> = ({
  userMetadata,
  setIsProfileModalOpen,
  setUserMetadata,
}) => {
  const {
    name, username = '', bio: userBio, socialLinks = [], title: userTitle,
  } = userMetadata ?? {};

  const [fullName, setFullName] = useState<string>(name || username);
  const [title, setTitle] = useState<string>(userTitle || '');
  const [bio, setBio] = useState<string>(userBio || '');
  const [linkedin, setLinkedin] = useState<string>(
    socialLinks?.find((link: { platform: string }) => link.platform === 'linkedin')?.socialLink || '',
  );
  const [x, setX] = useState<string>(
    socialLinks?.find((link: { platform: string }) => link.platform === 'x')?.socialLink
    || socialLinks?.find((link: { platform: string }) => link.platform === 'twitter')?.socialLink
    || '',
  );
  const [displayProfile, setDisplayProfile] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const intl = useIntl();

  const closePopup = () => setIsProfileModalOpen(false);

  const validateSocialLink = (platform: string, value: string): string => {
    if (!value) {
      return '';
    }
    const urlStubs = SOCIAL_PLATFORM_URLS[platform] ?? [];
    const platformLabel = SOCIAL_PLATFORM_LABELS[platform] ?? platform;
    if (value.startsWith('http')) {
      try {
        const url = new URL(value);
        if (!urlStubs.some(stub => url.href.includes(stub))) {
          return intl.formatMessage(messages.invalidSocialLink, { platform: platformLabel });
        }
      } catch {
        return intl.formatMessage(messages.invalidSocialLink, { platform: platformLabel });
      }
    }
    return '';
  };

  const validateForm = (field: string, value: string): string => {
    if (field === 'fullName') {
      if (!value.trim()) {
        return intl.formatMessage(messages.fullNameEmptyError);
      }
      if (value.length > FIELD_MAX_LENGTH.fullName) {
        return intl.formatMessage(messages.fullNameTooLongError);
      }
    }
    if (field === 'title') {
      if (value.length > FIELD_MAX_LENGTH.title) {
        return intl.formatMessage(messages.titleTooLongError);
      }
    }
    if (field === 'bio') {
      if (value.length > FIELD_MAX_LENGTH.bio) {
        return intl.formatMessage(messages.aboutTooLongError);
      }
    }
    if (field === 'linkedin' || field === 'x') {
      if (value.length > FIELD_MAX_LENGTH.socialLink) {
        return intl.formatMessage(messages.socialLinkTooLongError);
      }
      const socialError = validateSocialLink(field, value);
      if (socialError) {
        return socialError;
      }
    }
    return '';
  };

  const handleChange = (field: string, value: string) => {
    const fieldStateMap: Record<string, (v: string) => void> = {
      fullName: setFullName,
      title: setTitle,
      bio: setBio,
      linkedin: setLinkedin,
      x: setX,
    };

    fieldStateMap[field]?.(value);

    setErrors(prev => ({
      ...prev,
      [field]: validateForm(field, value),
    }));
  };

  const validateAll = (): boolean => {
    const fields = {
      fullName,
      title,
      bio,
      linkedin,
      x,
    };
    const newErrors: Record<string, string> = {};

    Object.entries(fields).forEach(([field, value]) => {
      const error = validateForm(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateAll()) {
      return;
    }

    setIsLoading(true);

    const newUserMetadata: any = {
      name: fullName,
      bio,
      social_links: [
        {
          platform: 'x',
          social_link: x,
        },
        {
          platform: 'linkedin',
          social_link: linkedin,
        },
      ],
      title,
    };

    accountsApi.updateUserMetaData(username, newUserMetadata, (resUserMetaData) => {
      const updatedUser = keysToCamel(resUserMetaData) as AuthenticatedUser;
      setUserMetadata(updatedUser);
      setIsLoading(false);
      closePopup();
      // eslint-disable-next-line no-alert
      window.alert(intl.formatMessage(messages.updateSuccess));
    }, (res: any) => {
      setIsLoading(false);
      // eslint-disable-next-line no-alert
      window.alert(intl.formatMessage(messages.errorOccurred));
      if (res?.field_errors) {
        Object.keys(res.field_errors).forEach((field) => {
          setErrors(prev => ({ ...prev, [field]: res?.field_errors[field].user_message }));
        });
      }
    });
  };

  if (isLoading) {
    return (
      <div id="edit-popup" className="active">
        <SpinLoader size={40} containerHeight="150px" />
      </div>
    );
  }

  return (
    <div id="edit-popup" className="active">
      <div className="edit-profile">
        <div className="top">
          <div>{intl.formatMessage(messages.modalTitle)}</div>
          <div>
            {/* eslint-disable-next-line */}
            <i className="fas fa-times close-edit-popup" onClick={closePopup} />
          </div>
        </div>
        <div className="edit-input">
          <label htmlFor="fullName">{intl.formatMessage(messages.fullNameLabel)}*</label>
          <input
            id="fullName"
            data-name="name"
            required
            type="text"
            value={fullName}
            onChange={(e) => handleChange('fullName', e.target.value)}
          />
          {errors.fullName && <span className="form-error">{errors.fullName}</span>}
        </div>
        <div className="edit-input em-display">
          <label htmlFor="displayProfile">{intl.formatMessage(messages.displayOnProfileLabel)}</label>
          <input
            data-name="display_email"
            type="checkbox"
            id="displayProfile"
            name="displayProfile"
            checked={displayProfile}
            onChange={() => setDisplayProfile(!displayProfile)}
          />
        </div>
        <div className="edit-input">
          <label htmlFor="title">{intl.formatMessage(messages.titleLabel)}</label>
          <input
            id="title"
            data-name="title"
            type="text"
            value={title}
            onChange={(e) => handleChange('title', e.target.value)}
          />
          {errors.title && <span className="form-error">{errors.title}</span>}
        </div>
        <div className="edit-input">
          <label htmlFor="bio">{intl.formatMessage(messages.aboutLabel)}</label>
          <textarea
            id="bio"
            data-name="bio"
            cols={30}
            rows={6}
            value={bio}
            onChange={(e) => handleChange('bio', e.target.value)}
          />
          {errors.bio && <span className="form-error">{errors.bio}</span>}
        </div>
        <div className="edit-input">
          <label htmlFor="linkedin">{intl.formatMessage(messages.linkedInLabel)}</label>
          <input
            id="linkedin"
            data-type="social"
            data-name="linkedin"
            type="text"
            value={linkedin}
            onChange={(e) => handleChange('linkedin', e.target.value)}
          />
          {errors.linkedin && <span className="form-error">{errors.linkedin}</span>}
        </div>
        <div className="edit-input">
          <label htmlFor="x">{intl.formatMessage(messages.xSocialLabel)}</label>
          <input
            id="x"
            data-type="social"
            data-name="x"
            type="text"
            value={x}
            onChange={(e) => handleChange('x', e.target.value)}
          />
          {errors.x && <span className="form-error">{errors.x}</span>}
        </div>
        <div className="bottom">
          <button
            type="button"
            className="cancel close-edit-popup"
            onClick={closePopup}
          >
            {intl.formatMessage(messages.cancelButton)}
          </button>
          <button
            type="button"
            className="save"
            onClick={handleSave}
          >
            {intl.formatMessage(messages.saveButton)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserEditPopUp;
